import {
  filteredSessions, groupSessions, matches, shortMeta, findTalent,
  timelineHours, timelineRows, timelineWidth,
} from '@ce/core';
import { trackColor } from '@ce/theme';
import { useTheme } from '../ThemeProvider';
import {
  dayStyle, divider, kicker, matchStyle, pill, saveStyle, segOpt, settle, t,
} from '../styles';
import type { ScreenProps } from './types';

function DayPills({ bundle, state, dispatch }: ScreenProps) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '2px 0 14px' }}>
      {bundle.days.map((d) => (
        <button
          key={d.key}
          type="button"
          onClick={() => dispatch({ type: 'setDay', day: d.key })}
          aria-pressed={state.day === d.key}
          style={dayStyle(state.day === d.key)}
        >
          <div style={{ font: '500 9px/1 var(--font-body)', letterSpacing: '0.14em', opacity: 0.65 }}>
            {d.dow}
          </div>
          <div style={{ font: '500 17px/1 var(--font-body)', marginTop: 5 }}>{d.num}</div>
        </button>
      ))}
    </div>
  );
}

export function Schedule(props: ScreenProps) {
  const { bundle, state, dispatch } = props;
  const theme = useTheme();
  const sessions = filteredSessions(bundle, state);
  const groups = groupSessions(sessions, state.schedView);

  return (
    <div className="stagger">
      <DayPills {...props} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 13, position: 'relative' }}>
        <div style={{ display: 'inline-flex', border: `1px solid ${divider}`, borderRadius: 8, overflow: 'hidden' }}>
          {(['time', 'room'] as const).map((view) => (
            <button
              key={view}
              type="button"
              onClick={() => dispatch({ type: 'setSchedView', view })}
              aria-pressed={state.schedView === view}
              style={segOpt(state.schedView === view)}
            >
              {view === 'time' ? 'By time' : 'By location'}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: t(0.42) }}>{sessions.length} sessions</div>
      </div>

      <div
        className="scroll"
        style={{ display: 'flex', gap: 6, overflowX: 'auto', margin: '0 -20px 16px', padding: '0 20px 2px' }}
      >
        {bundle.tracks.map((track) => (
          <button
            key={track}
            type="button"
            onClick={() => dispatch({ type: 'setTrack', track })}
            aria-pressed={state.track === track}
            style={pill(state.track === track)}
          >
            {track}
          </button>
        ))}
      </div>

      {groups.map((group) => (
        <div key={group.label} style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={kicker(11, 'var(--color-accent-300)', 0.14)}>{group.label}</div>
            <div
              style={{
                flex: 1, height: 1,
                background: `linear-gradient(to right, ${divider}, transparent)`,
              }}
            />
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {group.items.map((s, i) => {
              const on = state.saved.includes(s.id);
              const match = matches(state.interests, s.tags);
              const who = s.who.map((id) => findTalent(bundle, id).name).join(' · ');
              return (
                <div
                  key={s.id}
                  style={{
                    display: 'flex', gap: 12, padding: '13px 14px', borderRadius: 10,
                    background: 'var(--color-surface)', boxShadow: 'var(--shadow-sm)',
                    ...settle(i),
                  }}
                >
                  <div
                    style={{
                      width: 2, alignSelf: 'stretch', borderRadius: 2,
                      background: trackColor(theme, s.track), flex: 'none',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => dispatch({ type: 'openSession', id: s.id })}
                    style={{
                      flex: 1, minWidth: 0, textAlign: 'left', border: 0, padding: 0,
                      background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                      <span style={kicker(9.5, t(0.5), 0.12)}>{s.track}</span>
                      <span style={matchStyle(match)}>Your interests</span>
                    </div>
                    <div
                      style={{
                        font: '500 15px/1.25 var(--font-body)', margin: '7px 0 5px',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {s.title}
                    </div>
                    <div style={{ fontSize: 12, lineHeight: 1.5, color: t(0.55) }}>{shortMeta(s)}</div>
                    {who && (
                      <div style={{ fontSize: 11.5, lineHeight: 1.5, color: t(0.42), marginTop: 5 }}>{who}</div>
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label={on ? `Unstar ${s.title}` : `Star ${s.title}`}
                    onClick={() => dispatch({ type: 'toggleSave', id: s.id })}
                    style={saveStyle(on)}
                  >
                    {on ? '★' : '☆'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <div style={{ height: 26 }} />
    </div>
  );
}

/** Rooms down, clock across — for spotting gaps and clashes. */
export function Timeline(props: ScreenProps) {
  const { bundle, state, dispatch } = props;
  const theme = useTheme();
  const daySessions = bundle.sessions.filter((s) => s.day === state.day);
  const rows = timelineRows(daySessions);
  const hours = timelineHours();
  const width = timelineWidth();

  return (
    <div className="stagger">
      <DayPills {...props} />
      <div style={{ fontSize: 11, color: t(0.42), marginBottom: 12 }}>
        {daySessions.length} sessions across {rows.length} rooms · scroll sideways
      </div>

      <div
        className="scroll"
        style={{ margin: '0 -20px', overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}
      >
        <div style={{ minWidth: '100%', padding: '0 20px 8px' }}>
          <div style={{ position: 'relative', height: 16, width }}>
            {hours.map((h) => (
              <div
                key={h.label}
                style={{
                  position: 'absolute', left: h.left + 6, top: 0,
                  font: '400 10px/1 var(--font-mono)', color: t(0.4),
                }}
              >
                {h.label}
              </div>
            ))}
          </div>

          {rows.map((row) => (
            <div key={row.room} style={{ marginBottom: 4 }}>
              <div
                style={{
                  ...kicker(9.5, 'var(--color-accent-300)', 0.14),
                  position: 'sticky', left: 0, padding: '8px 0 6px',
                }}
              >
                {row.room}
              </div>
              <div
                style={{
                  position: 'relative', height: 82, width,
                  borderTop: '1px solid rgba(var(--color-text-rgb), 0.07)',
                }}
              >
                {hours.map((h) => (
                  <div
                    key={h.label}
                    style={{
                      position: 'absolute', top: 0, bottom: 0, left: h.left, width: 1,
                      background: 'rgba(var(--color-text-rgb), 0.07)',
                    }}
                  />
                ))}
                {row.blocks.map((b) => {
                  const on = state.saved.includes(b.session.id);
                  return (
                    <button
                      key={b.session.id}
                      type="button"
                      onClick={() => dispatch({ type: 'openSession', id: b.session.id })}
                      style={{
                        position: 'absolute', left: b.left, top: 6, width: b.width, height: 68,
                        padding: '8px 10px', borderRadius: 7, overflow: 'hidden', textAlign: 'left',
                        cursor: 'pointer',
                        border: `1px solid ${on ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.14)'}`,
                        background: on ? 'rgba(var(--color-accent-rgb), 0.2)' : 'var(--color-surface)',
                        color: 'var(--color-text)',
                        borderLeft: `2px solid ${trackColor(theme, b.session.track)}`,
                      }}
                    >
                      <div style={{ font: '400 9.5px/1 var(--font-mono)', color: t(0.5) }}>
                        {b.session.t}–{b.session.end}
                      </div>
                      <div
                        style={{
                          font: '500 12px/1.28 var(--font-body)', marginTop: 5,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                        }}
                      >
                        {b.session.title}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 11, lineHeight: 1.55, color: t(0.4), marginTop: 12 }}>
        {bundle.strings.scheduleGridHint}
      </div>
      <div style={{ height: 26 }} />
    </div>
  );
}
